import { useEffect, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { contentTypeOf, loadPostById, type FeedItem } from '../lib/feed'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'
import { AppShell } from '../components/AppShell'
import { ContestResults } from '../components/ContestResults'
import { FeedPost } from '../components/FeedPost'
import { Thread, POST_LABELS } from '../components/Thread'

/**
 * `#/item/:id` — ONE piece of content, the door a bell row opens (125, HLP-23).
 *
 * ── WHY IT RESOLVES THE TYPE HERE AND NOT IN THE BELL ───────────────────────
 *
 * A comment notification's kind says who it is for (`comment.on_your_post`,
 * `.mentioned`, `.reply_to_you`) and not what it was ON. A comment on a post
 * and a reply on a Wiki One question arrive as the same kind. So the bell
 * links every comment kind here, and this asks the database what the subject
 * is: a post renders with its thread open, a Wiki One item goes to the board
 * with that item opened, and anything the reader cannot see — removed,
 * hidden, another tenant's — says "no longer available" and stops. That
 * sentence is deliberately not more specific: the reason a row is invisible
 * is not the reader's to know (content_is_visible decides, and says nothing).
 *
 * ── A POST, NOT THE FEED SCROLLED TO A POST ─────────────────────────────────
 *
 * The feed is a LIMITED page (feed_page, 30 rows), so "open the feed and find
 * it" fails for exactly the older posts somebody is being told about now.
 * post_by_id returns the feed's own row shape, so this is the same card with
 * the same thread — never a second rendering of a post.
 */
export function Item({ me }: { me: Me }) {
  const { id = '' } = useParams()
  /* 127 (FED-25). A moderator's bell row about a reply the check flagged or
     took down arrives with ?review=1: the thread opens showing what the check
     said. Still gated on the permission as well — the flag in the URL is an
     offer a colleague could type, and check_notes returns them nothing. */
  const [params] = useSearchParams()
  const reviewing = params.get('review') === '1' && can(me, 'content.moderate')
  // 143 (FED-49). A photo.tagged bell row opens the viewer on that photo.
  const openPhotoPath = params.get('photo')
  const h = HOME[useHubLang()]
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'post'; post: FeedItem }
    | { kind: 'wiki' }
    | { kind: 'gone' }
  >({ kind: 'loading' })

  useEffect(() => {
    let live = true
    setState({ kind: 'loading' })
    ;(async () => {
      try {
        const type = await contentTypeOf(id)
        if (type === 'wiki_question' || type === 'wiki_answer') {
          if (live) setState({ kind: 'wiki' })
          return
        }
        const post = type === 'post' ? await loadPostById(id, me.locale) : null
        if (live) setState(post ? { kind: 'post', post } : { kind: 'gone' })
      } catch {
        // A failed read and an invisible row say the same thing to the reader:
        // there is nothing here they can open. Neither is worth an alert.
        if (live) setState({ kind: 'gone' })
      }
    })()
    return () => {
      live = false
    }
  }, [id, me.locale])

  if (state.kind === 'wiki') return <Navigate to={`/wiki?open=${id}`} replace />

  return (
    <AppShell
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      <div className="item">
        <p className="item__back">
          <Link to="/">← {h.backToHub}</Link>
        </p>
        {state.kind === 'loading' && (
          <p className="empty" aria-busy="true">
            {h.itemLoading}
          </p>
        )}
        {state.kind === 'gone' && <p className="empty">{h.itemGone}</p>}
        {state.kind === 'post' && (
          <FeedPost
            post={state.post}
            locale={me.locale}
            extra={<ContestResults contentId={state.post.contentId} />}
            initialThreadOpen
            openPhotoPath={openPhotoPath}
            thread={({ autoFocus }) => (
              <Thread contentId={state.post.contentId} me={me} labels={POST_LABELS} autoFocus={autoFocus} reviewing={reviewing} />
            )}
          />
        )}
      </div>
    </AppShell>
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
